import { useEffect, useRef } from "react";
import helpers from "helpers/index";
import axios, { AxiosInstance } from "axios";

const axiosDependency: AxiosInstance = axios.create({
    baseURL: process.env.REACT_APP_BACKEND_URL, // hoặc bỏ đi nếu dùng path tuyệt đối
    withCredentials: true,
    timeout: 15000,
    // KHÔNG thêm headers mặc định Authorization ở đây
});
// (tùy chọn) chặn thêm mọi interceptor ngoài app vô tình gắn:
axiosDependency.interceptors.request.clear?.();
axiosDependency.interceptors.response.clear?.();

/**
 * Hook làm mới Access Token thật sớm & thông minh
 * - Gọi ngay khi tab được focus lần đầu (và cả lúc mount nếu tab đang visible)
 * - Chủ động canh hạn và làm mới trước khi hết hạn (leeway)
 * - Tự pause khi tab ẩn, resume khi tab hiện
 * - Chống bão refresh giữa nhiều tab (BroadcastChannel + localStorage flag)
 * - Chống gọi trùng trong cùng tab (singleflight lock)
 */

// Làm mới trước khi hết hạn bao nhiêu ms
const REFRESH_BEFORE_MS = 5 * 60 * 1000; // 5 phút
// Kiểm tra định kỳ khi tab đang visible
const CHECK_INTERVAL_MS = 90 * 1000; // 90 giây (ngắn hơn leeway để an toàn)
// Khóa singleflight trong cùng tab
let inFlight = false;
// Chặn mount kép của React 18 StrictMode (dev) bằng singleton ở cấp module
let INSTALLED = false;
// Chống spam nếu 2 luồng cùng thắng race → bỏ qua nếu mới refresh thành công gần đây
let lastSuccessAt = 0; // ms epoch
// Broadcast giữa các tab
const bc: BroadcastChannel | null = typeof window !== "undefined" && "BroadcastChannel" in window
    ? new BroadcastChannel("auth")
    : null;

function getExpiryMs(): number | null {
    // Cookie 'EA' do backend set: epoch milliseconds (string)
    const str = helpers.cookie_get("EA");
    if (!str) return null;
    const n = Number(str);
    return Number.isFinite(n) ? n : null;
}

function shouldRefreshNow(nowMs: number, expMs: number | null): boolean {
    if (!expMs) return true; // không biết hạn → refresh sớm
    return nowMs + REFRESH_BEFORE_MS >= expMs;
}

export default function useRefreshTokenHelper() {

    const intervalRef = useRef<number | null>(null);

    // Cờ để biết lần đầu tab chuyển sang visible → gọi refresh sớm
    const firstVisibleRef = useRef(true);

    // Hàm thực sự gọi refresh (đã chống trùng & có sync nhiều tab)
    const doRefresh = async () => {
        // Nếu vừa refresh thành công < 5s trước → bỏ qua
        if (Date.now() - lastSuccessAt < 5000) return;
        if (inFlight) return; // đang refresh
        inFlight = true;
        try {
            localStorage.setItem("auth:refreshing", Date.now().toString());
            bc?.postMessage({ type: "refresh:start" });

            await axiosDependency.post<any>(`/session/refresh_token`).then((res) => res.data)


            lastSuccessAt = Date.now();
            localStorage.setItem("auth:last_refreshed", lastSuccessAt.toString());
            bc?.postMessage({ type: "refresh:done" });
        } catch (err) {
            console.error("Refresh token failed");
            bc?.postMessage({ type: "refresh:error" });
        } finally {
            inFlight = false;
            localStorage.removeItem("auth:refreshing");
        }
    };

    // Kiểm tra & refresh nếu cần (dùng trong interval / khi visible)
    const checkAndMaybeRefresh = async () => {
        const exp = getExpiryMs();
        const now = Date.now();
        if (shouldRefreshNow(now, exp)) {
            await doRefresh();
        }
    };

    useEffect(() => {
        // ✅ Chặn mount kép (StrictMode dev): nếu đã cài, không cài lại
        if (INSTALLED) return;
        INSTALLED = true;

        const onBC = (ev: MessageEvent) => {
            // Không cần xử lý nhiều, chỉ giữ hook tối giản nhiệm vụ refresh
            if (!ev?.data?.type) return;
        };
        bc?.addEventListener("message", onBC);

        const onStorage = (e: StorageEvent) => {
            // Không reset gì ở đây; interval sẽ kiểm tra tiếp
        };
        window.addEventListener("storage", onStorage);

        const handleVisibility = () => {
            const visible = document.visibilityState === "visible";
            if (visible) {
                if (firstVisibleRef.current) {
                    firstVisibleRef.current = false;
                    // Gọi sớm ở lần visible đầu tiên
                    checkAndMaybeRefresh();
                }
                if (intervalRef.current) window.clearInterval(intervalRef.current);
                intervalRef.current = window.setInterval(() => {
                    checkAndMaybeRefresh();
                }, CHECK_INTERVAL_MS) as unknown as number;
            } else {
                if (intervalRef.current) {
                    window.clearInterval(intervalRef.current);
                    intervalRef.current = null;
                }
            }
        };

        // ❗ Không gọi check ngay tại đây nữa để tránh trùng với handleVisibility
        // Chỉ đăng ký listener rồi tự kích 1 nhịp handleVisibility để thiết lập trạng thái ban đầu
        document.addEventListener("visibilitychange", handleVisibility);
        handleVisibility();

        return () => {
            document.removeEventListener("visibilitychange", handleVisibility);
            bc?.removeEventListener("message", onBC as any);
            window.removeEventListener("storage", onStorage);
            if (intervalRef.current) window.clearInterval(intervalRef.current);
            // Không reset INSTALLED để giữ singleton trong phiên dev
        };
    }, []);
}
